import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const Privacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: March 4, 2026</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Genia+ ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our 
              Service.
            </p>
            <p>
              Please read this Privacy Policy carefully. By using the Service, you agree to the collection 
              and use of information in accordance with this policy.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information</h3>
            <p>We collect the following personal information when you register:</p>
            <ul>
              <li>Name (first and last)</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Company name (optional)</li>
            </ul>

            <h3>2.2 Amazon Seller Information</h3>
            <p>When you connect your Amazon account, we collect:</p>
            <ul>
              <li>Amazon Seller ID</li>
              <li>Amazon Marketplace ID</li>
              <li>Amazon SP-API credentials (encrypted)</li>
              <li>Product data (SKU, ASIN, title, price, inventory)</li>
              <li>Order data (order ID, date, amount, status)</li>
              <li>Amazon Ads data (campaigns, keywords, metrics) - Pro plan only</li>
            </ul>

            <h3>2.3 Payment Information</h3>
            <p>
              Payment information is processed by Stripe. We do not store your credit card information. 
              We only store:
            </p>
            <ul>
              <li>Stripe customer ID</li>
              <li>Subscription status</li>
              <li>Billing history</li>
            </ul>

            <h3>2.4 Usage Information</h3>
            <p>We automatically collect:</p>
            <ul>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited</li>
              <li>Time and date of visits</li>
              <li>Time spent on pages</li>
              <li>Referring website</li>
            </ul>

            <h3>2.5 Cookies and Tracking</h3>
            <p>
              We use cookies and similar tracking technologies to track activity on our Service and hold 
              certain information. You can instruct your browser to refuse all cookies or to indicate 
              when a cookie is being sent.
            </p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for:</p>
            <ul>
              <li>Providing and maintaining the Service</li>
              <li>Synchronizing your Amazon seller data</li>
              <li>Calculating profits and analytics</li>
              <li>Providing AI-powered recommendations</li>
              <li>Processing payments and subscriptions</li>
              <li>Sending service-related emails (verification, notifications)</li>
              <li>Improving and optimizing the Service</li>
              <li>Detecting and preventing fraud</li>
              <li>Complying with legal obligations</li>
              <li>Providing customer support</li>
            </ul>
          </section>

          <section>
            <h2>4. Data Security</h2>
            
            <h3>4.1 Security Measures</h3>
            <p>We implement industry-standard security measures including:</p>
            <ul>
              <li>AES-256 encryption for sensitive data (Amazon credentials)</li>
              <li>Bcrypt password hashing</li>
              <li>HTTPS/TLS encryption for data in transit</li>
              <li>JWT authentication tokens</li>
              <li>Rate limiting and account locking</li>
              <li>Regular security audits</li>
              <li>Secure database access controls</li>
            </ul>

            <h3>4.2 Data Storage</h3>
            <p>
              Your data is stored on secure servers provided by MongoDB Atlas and Render. These providers 
              maintain SOC 2 Type II compliance and implement robust security measures.
            </p>

            <h3>4.3 Data Retention</h3>
            <p>
              We retain your data for as long as your account is active or as needed to provide the 
              Service. You can request deletion of your data at any time by contacting us.
            </p>
          </section>

          <section>
            <h2>5. Data Sharing and Disclosure</h2>
            
            <h3>5.1 Third-Party Services</h3>
            <p>We share data with the following third-party services:</p>
            <ul>
              <li><strong>Amazon SP-API:</strong> To synchronize your seller data</li>
              <li><strong>Stripe:</strong> To process payments</li>
              <li><strong>SendGrid:</strong> To send transactional emails</li>
              <li><strong>Groq:</strong> To provide AI-powered features</li>
            </ul>

            <h3>5.2 Legal Requirements</h3>
            <p>We may disclose your information if required to do so by law or in response to:</p>
            <ul>
              <li>Valid legal requests (subpoenas, court orders)</li>
              <li>Protecting our rights and property</li>
              <li>Preventing fraud or illegal activity</li>
              <li>Protecting the safety of users</li>
            </ul>

            <h3>5.3 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your information may be 
              transferred. We will notify you before your information is transferred and becomes subject 
              to a different Privacy Policy.
            </p>

            <h3>5.4 No Selling of Data</h3>
            <p>
              We do not sell, rent, or trade your personal information to third parties for marketing 
              purposes.
            </p>
          </section>

          <section>
            <h2>6. Your Rights</h2>
            
            <h3>6.1 Access and Correction</h3>
            <p>
              You have the right to access and update your personal information at any time through your 
              account settings.
            </p>

            <h3>6.2 Data Portability</h3>
            <p>
              You have the right to request a copy of your data in a machine-readable format.
            </p>

            <h3>6.3 Deletion</h3>
            <p>
              You have the right to request deletion of your account and all associated data. Contact us 
              at support@geniaplus.com to request deletion.
            </p>

            <h3>6.4 Opt-Out</h3>
            <p>
              You can opt out of marketing emails by clicking the unsubscribe link in any email. Note 
              that you cannot opt out of service-related emails (verification, billing, etc.).
            </p>

            <h3>6.5 GDPR Rights (EU Users)</h3>
            <p>If you are in the European Union, you have additional rights under GDPR:</p>
            <ul>
              <li>Right to be informed</li>
              <li>Right of access</li>
              <li>Right to rectification</li>
              <li>Right to erasure</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object</li>
              <li>Rights related to automated decision making</li>
            </ul>

            <h3>6.6 CCPA Rights (California Users)</h3>
            <p>If you are a California resident, you have rights under CCPA:</p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to say no to the sale of personal information</li>
              <li>Right to access your personal information</li>
              <li>Right to equal service and price</li>
            </ul>
          </section>

          <section>
            <h2>7. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you are a parent or guardian and believe 
              your child has provided us with personal information, please contact us.
            </p>
          </section>

          <section>
            <h2>8. International Data Transfers</h2>
            <p>
              Your information may be transferred to and maintained on servers located outside of your 
              state, province, country, or other governmental jurisdiction where data protection laws may 
              differ. By using the Service, you consent to such transfers.
            </p>
          </section>

          <section>
            <h2>9. Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date. You are 
              advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section>
            <h2>10. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <p>
              Email: privacy@geniaplus.com<br />
              Support: support@geniaplus.com<br />
              Address: [Your Business Address]
            </p>
          </section>

          <section>
            <h2>11. Data Processing Agreement</h2>
            <p>
              For business customers who require a Data Processing Agreement (DPA) for GDPR compliance, 
              please contact us at privacy@geniaplus.com.
            </p>
          </section>
        </div>

        <div className="legal-footer">
          <p>
            By using Genia+, you acknowledge that you have read and understood this Privacy Policy.
          </p>
          <div className="legal-links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/">Back to Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
